import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  OnChanges,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  SimpleChanges,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { MAT_SELECT_CONFIG, MatSelect } from '@angular/material/select';
import { MatSelectChange } from '@angular/material/select';

import { CourseCategoryHelper } from '@app/core/helper/course-category.helper';
import { ModelClass } from '@core/interfaces/model-class.type';
import { CourseCategory } from '@models/course-category.model';
import { Resource } from './interfaces';
import { ILTCourse } from '@app/models/ilt-course.model';
import { LearningPath } from '@app/models/learning-path.model';
import { Course } from '@app/models/course.model';
import { ILTCourseEvent } from '@app/models/ilt-course/ilt-course-event.model';
import { DatePipe } from '@angular/common';
import { Member } from '@app/models/member.model';
import { TrainingTag } from '@models/company/training-tag.model';
import { TranslateService } from '@ngx-translate/core';
import { multiSelectStartBottomTopPosition } from '@core/constants/multi-select-position.constant';

@Component({
  selector: 'app-multi-select-local-search',
  templateUrl: './multi-select-local-search.component.html',
  styleUrls: ['./multi-select-local-search.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,

  standalone: false,
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        overlayPanelClass: 'multi-select-local-select-overlay-panel',
      },
    },
  ],
})
export class MultiSelectLocalSearchComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;
  @Input() initialIds: string[] = [];
  @Input() className!: ModelClass<Resource>;
  @Input() placeholder = '';
  @Input() placeholderLabel = '';
  @Input() noEntriesFoundLabel = '';
  @Input() multiple = false;
  @Input() resources: Resource[] = [];
  @Input() selectAllDefault = false;
  @Input() selectIncludeChildren = true;
  @Input() multiSelectPosition = multiSelectStartBottomTopPosition;
  @Input() set disabled(value: boolean) {
    if (value) {
      this.multiLocalCtrl.disable();
    } else {
      this.multiLocalCtrl.enable();
    }
  }
  @Output() selectResources = new EventEmitter<string[]>();
  @Output() selectEvent = new EventEmitter<Resource[]>();
  sortedResources: Resource[] = [];
  selectNodeDepthClasses = {
    ROOT: 'root',
    CHILD: 'child',
    GRANDCHILD: 'grandchild',
  };
  public multiLocalCtrl: UntypedFormControl = new UntypedFormControl();
  public multiLocalFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public filteredResourcesMulti: ReplaySubject<Resource[]> = new ReplaySubject<
    Resource[]
  >(1);
  private onDestroy = new Subject<void>();
  private previousSelectedIds: string[] = [];

  constructor(
    private datePipe: DatePipe,
    public elementRef: ElementRef,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.multiLocalFilterCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.filterResourcesMulti();
      });
  }

  ngAfterViewInit(): void {
    this.setInitialValue();

    if (this.multiSelectPosition) {
      this.multiSelect._positions = [
        {
          originX: this.multiSelectPosition.originX,
          originY: this.multiSelectPosition.originY,
          overlayX: this.multiSelectPosition.overlayX,
          overlayY: this.multiSelectPosition.overlayY,
        },
      ];
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.selectAllDefault && changes['resources']) {
      this.multiLocalCtrl.setValue(this.resources);
    }

    if (this.className === CourseCategory) {
      this.sortedResources = CourseCategoryHelper.sortCourseCategories(
        this.resources as CourseCategory[],
      );
      this.applyInitialSelection();
    } else if (
      this.className === ILTCourse ||
      this.className === LearningPath ||
      this.className === Course ||
      this.className === ILTCourseEvent ||
      this.className === Member ||
      this.className === TrainingTag
    ) {
      this.sortedResources = this.resources;
      this.applyInitialSelection();
    } else {
      this.sortedResources = this.resources;
    }

    this.filterResourcesMulti();
  }

  selectionChanged(selectedResources: MatSelectChange): void {
    const currentResources = selectedResources.value as Resource[];

    if (this.className === CourseCategory && this.selectIncludeChildren) {
      const currentIds = currentResources.map(r => r.id);
      const adjustedIds = CourseCategoryHelper.applyCategoryCascade(
        this.previousSelectedIds,
        currentIds,
        this.resources as CourseCategory[],
      );

      const adjustedSet = new Set(adjustedIds);
      const adjustedResources = this.resources.filter(r =>
        adjustedSet.has(r.id),
      );
      const changed =
        adjustedResources.length !== currentResources.length ||
        adjustedIds.some(id => !currentIds.includes(id));

      if (changed) {
        this.multiLocalCtrl.setValue(adjustedResources, { emitEvent: false });
      }

      this.previousSelectedIds = adjustedIds;
      this.selectResources.emit(adjustedIds);
      this.selectEvent.emit(adjustedResources);
      return;
    }

    const selectedIds = currentResources.map(r => r.id);
    this.previousSelectedIds = selectedIds;
    this.selectResources.emit(selectedIds);
    this.selectEvent.emit(currentResources);
  }

  getNodeDepthClass(resource: Resource): string {
    if (this.className !== CourseCategory) {
      return this.selectNodeDepthClasses.ROOT;
    }

    const courseCategory = resource as CourseCategory;

    if (
      !courseCategory.parentId ||
      CourseCategoryHelper.hasNoAvailableAncestry(
        this.resources as CourseCategory[],
        courseCategory,
      )
    ) {
      return this.selectNodeDepthClasses.ROOT;
    }

    if (
      courseCategory.ancestry?.split('/').length === 1 ||
      !this.resources.find(cC => cC.id === courseCategory.parentId?.toString())
    ) {
      return this.selectNodeDepthClasses.CHILD;
    }

    return this.selectNodeDepthClasses.GRANDCHILD;
  }

  getColor(resource: Resource): string {
    return this.className === TrainingTag
      ? ((resource as TrainingTag).color ?? '')
      : '';
  }

  getName(resource: Resource): string {
    if (this.className === ILTCourseEvent) {
      return resource.eventName
        ? resource.eventName
        : this.formatEventDate(resource.startDate);
    }
    if (this.className === Member) {
      return resource.name + ' (' + resource.identifier + ')';
    }
    return resource.name;
  }

  private formatEventDate(date: string | Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = this.translateService.instant(
      `common.monthsShort.month${d.getMonth()}`,
    );
    return `${day} ${month}`;
  }

  private applyInitialSelection(): void {
    if (
      !this.initialIds ||
      !this.initialIds.length ||
      !this.sortedResources.length
    ) {
      return;
    }

    const initialResources = this.sortedResources.filter(
      resource => this.initialIds.indexOf(resource.id) > -1,
    );
    this.multiLocalCtrl.setValue(initialResources);
    this.previousSelectedIds = initialResources.map(r => r.id);
  }

  private setInitialValue(): void {
    this.filteredResourcesMulti
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe(() => {
        //  the option value is the id, hence the string comparison
        this.multiSelect.compareWith = (a: string, b: string) =>
          !!a && !!b && a === b;
      });
  }

  private filterResourcesMulti(): void {
    if (!this.sortedResources) {
      return;
    }
    let search = this.multiLocalFilterCtrl.value;
    if (!search) {
      this.filteredResourcesMulti.next(this.sortedResources.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredResourcesMulti.next(
      this.sortedResources.filter(resource => {
        if (this.className === ILTCourseEvent) {
          const name = resource.eventName
            ? resource.eventName
            : this.formatEventDate(resource.startDate);
          return name.toLowerCase().indexOf(search) > -1;
        } else {
          return resource.name.toLowerCase().indexOf(search) > -1;
        }
      }),
    );
  }
}
